import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AxiosError } from 'axios'

import showErrorToast from '@/common/utils/showErrorToast'
import useMutationUpdateDailyCheckIn from '@/modules/promotion/api/hooks/useMutateUpdateDailyCheckIn'
import { UpdateDailyCheckInRequest } from '@/modules/promotion/type'
import useQueryGetDailyCheckInById from '@/modules/promotion/api/hooks/useQueryGetDailyCheckInById'
import Spinner from '@/common/components/Spinner'
import DailyCheckInForm from '../../../DailyCheckInForm'
import { DailyCheckInFormValues } from '../../../DailyCheckInForm/components/FormSchema'
import { useQueryClient } from '@tanstack/react-query'
import { DailyCheckInQueryKeys } from '@/modules/promotion/keys'
import { ProviderResolutionConflictDialog } from '../../../ProviderResolutionConflictDialog'

export default function EditDailyCheckInForm() {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { id } = useParams()
	const { data: dailyCheckIn, isLoading } = useQueryGetDailyCheckInById(id!)
	const [conflictDialogOpen, setConflictDialogOpen] = useState(false)
	const [conflictMessage, setConflictMessage] = useState('')

	const { mutate: updateDailyCheckIn } = useMutationUpdateDailyCheckIn({
		onSuccess: () => {
			toast.success('แก้ไขกิจกรรมเช็คอินรายวันสำเร็จ')
			queryClient.invalidateQueries({
				queryKey: [DailyCheckInQueryKeys.GetDailyCheckIn],
			})
			queryClient.invalidateQueries({
				queryKey: [DailyCheckInQueryKeys.GetDailyCheckInById, id],
			})
			navigate('/operator/daily-check-in')
		},
		onError: (error) => {
			const axiosError = error as unknown as AxiosError<{
				message: string
				errorCode: string
			}>

			// Check if it's a provider resolution conflict error
			if (
				axiosError.isAxiosError &&
				axiosError.response?.data?.errorCode === 'PROVIDER_RESOLUTION_CONFLICT'
			) {
				setConflictMessage(axiosError.response.data.message)
				setConflictDialogOpen(true)
				return
			}

			// For other errors, show toast
			showErrorToast(error)
		},
	})

	const handleSubmit = (data: DailyCheckInFormValues) => {
		updateDailyCheckIn({
			id: id!,
			payload: data as unknown as UpdateDailyCheckInRequest,
		})
	}

	if (isLoading || !dailyCheckIn) return <Spinner />

	return (
		<>
			<DailyCheckInForm
				mode="edit"
				defaultValues={
					dailyCheckIn as unknown as Partial<DailyCheckInFormValues>
				}
				onSubmit={handleSubmit}
			/>
			<ProviderResolutionConflictDialog
				isOpen={conflictDialogOpen}
				onClose={() => setConflictDialogOpen(false)}
				message={conflictMessage}
			/>
		</>
	)
}
